import React, { useState, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  IconButton,
  Box,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  Grid,
} from "@material-ui/core";
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  Close as CloseIcon,
  AccountTreeOutlined as AccountTreeIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { getStandardButtonStyles } from "../../styles/standardButtons";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  dialogPaper: {
    borderRadius: "16px !important",
    border: `1px solid ${theme.palette.primary.main} !important`,
    boxShadow: theme.palette.type === "dark" 
      ? `0 0 20px ${theme.palette.primary.main}80 !important` 
      : `0 0 16px ${theme.palette.primary.main}40 !important`,
    backgroundColor: theme.palette.type === "dark" ? "#1E2126 !important" : "#FFFFFF !important",
    color: theme.palette.type === "dark" ? "#E2E8F0" : theme.palette.text.primary,
    overflow: "hidden !important",
    display: "flex !important",
    flexDirection: "column !important",
    maxWidth: 760,
    width: "100%",
  },
  dialogHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    "& h2": {
      display: "flex",
      alignItems: "center",
      fontSize: "1.15rem",
      fontWeight: 600,
      margin: 0,
      color: theme.palette.type === "dark" ? "#FFFFFF" : theme.palette.text.primary,
    },
  },
  dialogContent: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
    ...theme.scrollbarStyles,
  },
  dialogActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.02)",
    gap: 8,
  },
  fullWidthMenu: {
    maxWidth: "none !important",
  },
  phraseContainer: {
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: "10px",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)",
  },
  addPhraseButton: {
    alignSelf: "flex-start",
    marginTop: theme.spacing(1),
    borderRadius: 8,
    fontWeight: 600,
    fontSize: "0.82rem",
    textTransform: "none",
    padding: "6px 16px",
    color: theme.palette.type === "dark" ? "#FFFFFF" : theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}80`,
    backgroundColor: `${theme.palette.primary.main}1A`,
    boxShadow: `0 0 8px ${theme.palette.primary.main}26`,
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}2E`,
      border: `1.5px solid ${theme.palette.primary.main}`,
      boxShadow: `0 0 14px ${theme.palette.primary.main}80`,
    },
  },
  buttonProgress: {
    color: theme.palette.primary.main,
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -10,
    marginLeft: -10,
  },
  whatsappChip: {
    margin: "2px",
    backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)",
    color: theme.palette.type === "dark" ? "#FFFFFF" : theme.palette.text.primary,
  },
}));

const FlowSetupModal = ({ open, onClose, flowId, onSave }) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [dataItem, setDataItem] = useState({ name: "", brandId: "", kanbanId: "" });
  const [phrases, setPhrases] = useState([{ text: "", type: "exact" }]);
  const [selectedWhatsapps, setSelectedWhatsapps] = useState([]);
  
  const [brands, setBrands] = useState([]);
  const [kanbans, setKanbans] = useState([]);
  const [whatsApps, setWhatsApps] = useState([]);

  useEffect(() => {
    isMounted.current = true;
    const initData = async () => {
      if (open) {
        setLoading(true);
        resetForm();
        const [, , wppList] = await Promise.all([getBrands(), getKanbans(), getWhatsApps()]);
        if (flowId) {
          await getFlowDetails(wppList);
        }
        setLoading(false);
      }
    };
    initData();
    return () => { isMounted.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, flowId]);

  const resetForm = () => {
    setDataItem({ name: "", brandId: "", kanbanId: "" });
    setPhrases([{ text: "", type: "exact" }]);
    setSelectedWhatsapps([]);
  };

  const getBrands = async () => {
    try {
      const response = await api.get("/brands");
      const list = response.data || [];
      setBrands(list);
      if (list.length > 0 && !dataItem.brandId) {
         setDataItem(p => ({...p, brandId: list[0].id}));
      }
      return list;
    } catch (e) { 
      console.error(e); 
      return [];
    }
  };

  const getKanbans = async () => {
    try {
      const response = await api.get("/kanban-boards");
      const list = response.data || [];
      setKanbans(list);
      return list;
    } catch (e) { 
      console.error(e); 
      return [];
    }
  };

  const getWhatsApps = async () => {
    try {
      const response = await api.get("/whatsapp");
      const list = response.data || [];
      setWhatsApps(list);
      return list;
    } catch (e) { 
      console.error(e); 
      return []; 
    }
  };

  const getFlowDetails = async (availableWhatsapps = []) => {
    try {
      const response = await api.get(`/flowbuilder/${flowId}`);
      const flow = response.data.flow || response.data;
      if (!isMounted.current) return;
      setDataItem({ name: flow.name || "", brandId: flow.brandId || "", kanbanId: flow.kanbanId || "" });
      if (flow.whatsappIds && Array.isArray(flow.whatsappIds)) {
        const validIds = flow.whatsappIds.filter(id => availableWhatsapps.some(w => w.id === id));
        setSelectedWhatsapps(validIds);
      }
      if (flow.phrase) {
        let parsed = [];
        try {
          parsed = typeof flow.phrase === "string" ? JSON.parse(flow.phrase) : flow.phrase;
          if (!Array.isArray(parsed)) parsed = [parsed];
        } catch(e) {}
        setPhrases(parsed.length > 0 ? parsed : [{ text: "", type: "exact" }]);
      }
    } catch (e) { 
      toast.error(i18n.t("flowSetupModal.toasts.loadError") || "Error al cargar datos del flujo"); 
    }
  };

  const handleSave = async () => {
    if (!dataItem.name.trim()) {
      toast.error(i18n.t("flowSetupModal.toasts.nameRequired") || "El nombre es obligatorio");
      return;
    }
    if (!dataItem.brandId) {
      toast.error(i18n.t("flowSetupModal.toasts.brandRequired") || "Debe seleccionar una marca obligatoriamente");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: dataItem.name.trim(),
        brandId: dataItem.brandId || null,
        kanbanId: dataItem.kanbanId || null,
        whatsappIds: selectedWhatsapps,
        phrase: phrases.filter(p => p.text.trim())
      };
      if (flowId) {
        payload.flowId = flowId;
        await api.put("/flowbuilder", payload);
        toast.success(i18n.t("flowSetupModal.toasts.updated") || "Flujo actualizado con éxito");
        onSave && onSave(payload.name);
        onClose();
      } else {
        const res = await api.post("/flowbuilder", payload);
        toast.success(i18n.t("flowSetupModal.toasts.created") || "Flujo creado con éxito");
        onSave && onSave(payload.name);
        onClose();
        history.push(`/flowbuilder/${res.data.id}`);
      }
    } catch (error) {
      toastError(error);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      disableEscapeKeyDown={loading}
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      <div className={classes.dialogHeader}>
        <h2>
          <AccountTreeIcon style={{ marginRight: 10, fontSize: 24, color: "#00b4d8" }} />
          {flowId 
            ? (i18n.t("flowSetupModal.titleEdit") || "Editar Configuración del Flujo") 
            : (i18n.t("flowSetupModal.titleNew") || "Nuevo Flujo")}
        </h2>
        <Box display="flex" alignItems="center">
          <Typography
            variant="caption"
            style={{ opacity: 0.6, marginRight: 8, fontWeight: 500 }}
          >
            BotMix | Flujo
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Cerrar">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </div>

      <DialogContent dividers className={classes.dialogContent}>
        <TextField
          label={i18n.t("flowSetupModal.flowName") || "Nombre del Flujo"}
          value={dataItem.name}
          onChange={(e) => setDataItem({ ...dataItem, name: e.target.value })}
          fullWidth
          variant="outlined"
          disabled={loading}
        />
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>{i18n.t("flowSetupModal.brand") || "Marca"}</InputLabel>
              <Select
                value={dataItem.brandId}
                onChange={(e) => setDataItem({ ...dataItem, brandId: e.target.value })}
                label={i18n.t("flowSetupModal.brand") || "Marca"}
                MenuProps={{
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                  getContentAnchorEl: null,
                  classes: { paper: classes.fullWidthMenu }
                }}
                disabled={loading}
              >
                <MenuItem value=""><em>{i18n.t("flowSetupModal.none") || "Ninguno"}</em></MenuItem>
                {brands.map(b => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>{i18n.t("flowSetupModal.kanban") || "Embudo (Opcional)"}</InputLabel>
              <Select
                value={dataItem.kanbanId}
                onChange={(e) => setDataItem({ ...dataItem, kanbanId: e.target.value })}
                label={i18n.t("flowSetupModal.kanban") || "Embudo (Opcional)"}
                MenuProps={{
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                  getContentAnchorEl: null,
                  classes: { paper: classes.fullWidthMenu }
                }}
                disabled={loading}
              >
                <MenuItem value=""><em>{i18n.t("flowSetupModal.none") || "Ninguno"}</em></MenuItem>
                {kanbans.filter(k => !dataItem.brandId || k.brandId === dataItem.brandId).map(k => (
                  <MenuItem key={k.id} value={k.id}>
                    {k.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <FormControl fullWidth variant="outlined">
          <InputLabel>{i18n.t("flowSetupModal.whatsappConnections") || "Conexiones WhatsApp"}</InputLabel>
          <Select
            multiple
            value={selectedWhatsapps}
            onChange={(e) => setSelectedWhatsapps(e.target.value)}
            label={i18n.t("flowSetupModal.whatsappConnections") || "Conexiones WhatsApp"}
            MenuProps={{
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
              getContentAnchorEl: null,
              classes: { paper: classes.fullWidthMenu }
            }}
            disabled={loading}
            renderValue={(selected) => (
              <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                {selected.map(id => {
                  const w = whatsApps.find(wx => wx.id === id);
                  return (
                    <Chip 
                      key={id} 
                      label={w ? w.name : `ID: ${id}`} 
                      size="small" 
                      className={classes.whatsappChip}
                    />
                  );
                })}
              </Box>
            )}
          >
            {whatsApps.filter(w => !dataItem.brandId || w.brandId === dataItem.brandId).map(w => (
              <MenuItem key={w.id} value={w.id}>
                <Checkbox checked={selectedWhatsapps.indexOf(w.id) > -1} color="primary" />
                <ListItemText primary={`${w.name} (${w.status})`} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <Divider style={{ margin: "8px 0" }} />
        
        <Box display="flex" alignItems="center">
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            {i18n.t("flowSetupModal.triggersTitle") || "Palabras Claves (Triggers)"}
          </Typography>
        </Box>

        {phrases.map((phrase, idx) => (
          <Box key={idx} className={classes.phraseContainer}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={7}>
                <TextField
                  label={`${i18n.t("flowSetupModal.phraseLabel") || "Frase"} ${idx + 1}`}
                  value={phrase.text}
                  onChange={(e) => setPhrases(phrases.map((p, i) => i === idx ? { ...p, text: e.target.value } : p))}
                  fullWidth
                  variant="outlined"
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={phrases.length > 1 ? 10 : 12} sm={phrases.length > 1 ? 4 : 5}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>{i18n.t("flowSetupModal.condition") || "Condición"}</InputLabel>
                  <Select
                    value={phrase.type}
                    onChange={(e) => setPhrases(phrases.map((p, i) => i === idx ? { ...p, type: e.target.value } : p))}
                    label={i18n.t("flowSetupModal.condition") || "Condición"}
                    MenuProps={{
                      anchorOrigin: { vertical: "bottom", horizontal: "left" },
                      transformOrigin: { vertical: "top", horizontal: "left" },
                      getContentAnchorEl: null,
                      classes: { paper: classes.fullWidthMenu }
                    }}
                    disabled={loading}
                  >
                    <MenuItem value="exact">{i18n.t("flowSetupModal.exact") || "Exacta"}</MenuItem>
                    <MenuItem value="partial">{i18n.t("flowSetupModal.partial") || "Contiene"}</MenuItem>
                    <MenuItem value="not_contains">{i18n.t("flowSetupModal.notContains") || "No Contiene"}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {phrases.length > 1 && (
                <Grid item xs={2} sm={1} style={{ textAlign: "center" }}>
                  <IconButton 
                    onClick={() => setPhrases(phrases.filter((_, i) => i !== idx))} 
                    style={{ color: "#ff5252" }}
                    disabled={loading}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              )}
            </Grid>
          </Box>
        ))}

        <Button 
          variant="outlined" 
          startIcon={<AddIcon />} 
          onClick={() => setPhrases([...phrases, { text: "", type: "exact" }])} 
          className={classes.addPhraseButton}
          disabled={loading}
        >
          {i18n.t("flowSetupModal.addPhrase") || "Añadir Frase"}
        </Button>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={onClose}
          disabled={loading}
          className={classes.secondaryButton}
        >
          {i18n.t("flowSetupModal.cancel") || "Cancelar"}
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading}
          className={classes.applyButton}
        >
          {loading ? <CircularProgress size={20} className={classes.buttonProgress} /> : (i18n.t("flowSetupModal.save") || "Guardar")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowSetupModal;
